import * as React from "react";
import { Link } from "react-router-dom";
import { GapLinkProjectRequestModel } from "@app/projects/network-assessment/adequacy";
import { Resource } from "@app/resources";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { JSX } from "react";
import { formatDateTime } from "@app/shared/formatDateTime";
import ProjectService from "@app/projects/projectService";
import { QESButton } from "@app/shared/qes-standards/Button";

interface CrossProjectGapsProjectViewProps {
  asOfDate: Date;
  filterPath: string;
  gapLinkId: number;
  onBreadCrumbClick: () => void;
  projectResources: Resource[];
  regionDisplayName: string;
  specialtyGroupDisplay: string;
}

interface CrossProjectGapsProjectViewState {
  loading: boolean;
  numberOfDisplayedRecords: number;
  sortedRecords: CrossProjectGapsProjectViewTableData[];
}

interface CrossProjectGapsProjectViewTableData {
  [wafflesKeyColumnName]: string;
  projects: JSX.Element;
}

const columnConfig: ColumnConfig = {
  projects: {
    displayText: "Projects",
    testId: "projects",
  },
};

export default class CrossProjectGapsProjectView extends React.Component<CrossProjectGapsProjectViewProps, CrossProjectGapsProjectViewState> {
  private readonly loadMoreIncrement = 100;
  private readonly projectService = new ProjectService();

  constructor(props: CrossProjectGapsProjectViewProps) {
    super(props);

    this.state = {
      loading: true,
      numberOfDisplayedRecords: this.loadMoreIncrement,
      sortedRecords: [],
    };
  }

  public async componentDidMount() {
    const gapLinkRequestModel = {
      gapLinkId: this.props.gapLinkId,
      projectIds: this.props.projectResources.map((pr) => pr.resourceId),
    } as GapLinkProjectRequestModel;

    const gapLinkProjects = await this.projectService.getGapLinkProjects(gapLinkRequestModel);
    const records: CrossProjectGapsProjectViewTableData[] = gapLinkProjects.map((gapLinkProject) => {
      let tileUrl = `/projects/${gapLinkProject.customProjectId}/adequacy`;
      if (this.props.filterPath) {
        tileUrl += this.props.filterPath;
      }
      tileUrl += `?projectRunConfigurationType=${gapLinkProject.projectRunConfigurationType}`;
      return {
        projects: <Link key={gapLinkProject.projectName} to={tileUrl}>{gapLinkProject.projectName}</Link>,
        [wafflesKeyColumnName]: gapLinkProject.customProjectId.toString(),
      };
    });

    this.setState({ loading: false, sortedRecords: records });
  }

  public render() {
    return (
      <>
        <p>{`Projects with gaps in ${this.props.regionDisplayName} for ${this.props.specialtyGroupDisplay}`}</p>
        <div>
          <QESButton
            className="p-0 text-info mb-3"
            qesType="secondary"
            qesTestId="cross-project-gaps-project-view__previous"
            onClick={this.props.onBreadCrumbClick}
          >
            Return to Overview
          </QESButton>
        </div>
        {this.props.asOfDate && <small className="pull-right">As of {formatDateTime(this.props.asOfDate)}</small>}
        <small>Displaying {Math.min(this.state.numberOfDisplayedRecords, this.state.sortedRecords.length)} of {this.state.sortedRecords.length} rows</small>
        <Waffles<CrossProjectGapsProjectViewTableData>
          columns={columnConfig}
          customSort={this.customSort}
          data={this.state.sortedRecords.slice(0, this.state.numberOfDisplayedRecords)}
          loading={this.state.loading}
          fullWidth={true}
          loadMore={!(this.state.numberOfDisplayedRecords >= this.state.sortedRecords.length) ? this.handleLoadMore : undefined}
          testId="cross-project-gaps-project-view"
        />
      </>
    );
  }

  private handleLoadMore = (justChecking: boolean) => {
    const numberOfDisplayedRecords = this.state.numberOfDisplayedRecords + this.loadMoreIncrement;
    if (!justChecking) {
      this.setState({
        numberOfDisplayedRecords,
      });
    }
    return !(numberOfDisplayedRecords >= this.state.sortedRecords.length);
  }

  private customSort = (records: CrossProjectGapsProjectViewTableData[], column: keyof CrossProjectGapsProjectViewTableData, direction: SortDirections) => {
    // there's only one column in this table
    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    return records.sort((a, b) => (a.projects.key > b.projects.key ? 1 : -1) * multiplier);
  }
}
